import type {
  FilterNode,
  PredicateNode,
  RangeNode,
  AndNode,
  OrNode,
  NotNode,
  ComparisonOperator,
} from "./nodes";

/**
 * Creates an AND node that requires all children to be true
 */
export function and(...children: FilterNode[]): AndNode {
  return {
    type: "and",
    children,
  };
}

/**
 * Creates an OR node where at least one child must be true
 */
export function or(...children: FilterNode[]): OrNode {
  return {
    type: "or",
    children,
  };
}

/**
 * Creates a NOT node that negates the child expression
 */
export function not(child: FilterNode): NotNode {
  return {
    type: "not",
    child,
  };
}

/**
 * Creates a predicate node with a custom operator
 */
export function predicate(
  field: string,
  operator: ComparisonOperator,
  value: string | number | boolean | string[] | number[],
): PredicateNode {
  return {
    type: "predicate",
    field,
    operator,
    value,
  };
}

/**
 * Creates an equality predicate (field == value)
 */
export function eq(
  field: string,
  value: string | number | boolean,
): PredicateNode {
  return predicate(field, "===", value);
}

/**
 * Creates a not-equal predicate (field != value)
 */
export function neq(
  field: string,
  value: string | number | boolean,
): PredicateNode {
  return predicate(field, "!==", value);
}

/**
 * Creates a greater-than predicate (field > value)
 */
export function gt(field: string, value: number): PredicateNode {
  return predicate(field, ">", value);
}

/**
 * Creates a greater-than-or-equal predicate (field >= value)
 */
export function gte(field: string, value: number): PredicateNode {
  return predicate(field, ">=", value);
}

/**
 * Creates a less-than predicate (field < value)
 */
export function lt(field: string, value: number): PredicateNode {
  return predicate(field, "<", value);
}

/**
 * Creates a less-than-or-equal predicate (field <= value)
 */
export function lte(field: string, value: number): PredicateNode {
  return predicate(field, "<=", value);
}

/**
 * Creates a range node for numeric range queries
 */
export function range(
  field: string,
  options: {
    gt?: number;
    gte?: number;
    lt?: number;
    lte?: number;
  },
): RangeNode {
  return {
    type: "range",
    field,
    ...options,
  };
}

/**
 * Facet input type: maps field names to arrays of values
 */
export type FacetInput = Record<string, (string | number)[]>;

/**
 * Converts facet input to filter AST
 * For input { a: ['x','y'], b: ['l'] } creates:
 * AndNode([
 *   OrNode([ eq(a,'x'), eq(a,'y') ]),
 *   OrNode([ eq(b,'l') ])
 * ])
 */
export function fromFacets(facets: FacetInput): FilterNode | null {
  const entries = Object.entries(facets).filter(
    ([_, values]) => values.length > 0,
  );

  if (entries.length === 0) {
    return null;
  }

  const conditions = entries.map(([field, values]) => {
    if (values.length === 1) {
      return eq(field, values[0]);
    }
    return or(...values.map((value) => eq(field, value)));
  });

  if (conditions.length === 1) {
    return conditions[0];
  }

  return and(...conditions);
}
